import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { apiClient } from '@/api/client';
import { API_MAIL_MESSAGE_IDS } from '@/api/endpoints';
import { MAIL_MESSAGE_IDS_QUERY_KEY } from '@/api/queryKeys';
import type { MailFolderType, MailSearchType } from '@/types/components';
import { unwrapAxiosResponse } from '@/utils/axios';

const MESSAGES_PER_PAGE = 20;

const splitIntoPages = (messageIds: number[]): number[][] => {
  const pages: number[][] = [];

  for (let index = 0; index < messageIds.length; index += MESSAGES_PER_PAGE) {
    pages.push(messageIds.slice(index, index + MESSAGES_PER_PAGE));
  }

  return pages;
};

interface Params {
  folderType: MailFolderType | null;
  searchType: MailSearchType | null;
  searchValue: string;
}

export const useMailMessageIds = ({
  folderType,
  searchType,
  searchValue,
}: Params) => {
  const { data: messageIds, isFetching } = useQuery<number[]>({
    queryKey: [
      ...MAIL_MESSAGE_IDS_QUERY_KEY,
      folderType,
      searchValue && searchType,
      searchValue,
    ],
    queryFn: () =>
      apiClient
        .get<number[]>(API_MAIL_MESSAGE_IDS, {
          params: {
            'request.folderType': folderType,
            'request.searchType': searchValue ? searchType : undefined,
            'request.searchValue': searchValue || undefined,
          },
        })
        .then(unwrapAxiosResponse),
    enabled: !!folderType,
    retry: false,
  });

  const idPages = useMemo(() => splitIntoPages(messageIds ?? []), [messageIds]);

  return { idPages, isFetching };
};
